/**
 * Public marketing FAQ copy for /faq (both clients and stylists on one page).
 */

export type MarketingFaqAnswerSegment =
  | { kind: 'text'; text: string }
  | { kind: 'applyLink' };

export type MarketingFaqItem =
  | {
      question: string;
      answer: string;
      answerSegments?: never;
    }
  | {
      question: string;
      answerSegments: MarketingFaqAnswerSegment[];
      answer?: never;
    };

export interface MarketingFaqCategory {
  label: string;
  items: MarketingFaqItem[];
}

export interface MarketingFaqAudience {
  title: string;
  categories: MarketingFaqCategory[];
}

export const MARKETING_FAQ_AUDIENCES: MarketingFaqAudience[] = [
  {
    title: 'CLIENTS:',
    categories: [
      {
        label: 'GETTING STARTED',
        items: [
          {
            question: 'Where is EMME available?',
            answer:
              'EMME is currently available in the United States on Apple iOS.',
          },
          {
            question: 'What types of sessions are available?',
            answer:
              'EMME offers three ways to work with a stylist:\n\nInstant Virtual Consult —> connect with an available stylist in real time.\n\nScheduled Virtual Consult —> book a video session at a time that works for you.\n\nIn-Person Consult —> available in select cities, sessions take place at a location you provide.',
          },
          {
            question: 'How do I book a session?',
            answer:
              'Browse or filter stylists by availability, style, price, and more — then select a session type and book directly in the app.',
          },
          {
            question: 'Can I book a same-day session?',
            answer:
              'Yes, same-day sessions are available. Note that same-day bookings are non-cancelable.',
          },
        ],
      },
      {
        label: 'SESSIONS',
        items: [
          {
            question: 'How do I join a session?',
            answer:
              'For virtual sessions, navigate to the Upcoming Sessions tab in the app and select Join Call. All video sessions take place directly within EMME. In-person sessions start automatically — no need to join.',
          },
          {
            question: 'What is Instant Styling?',
            answer:
              'Tap "Get Styled Instantly" on the home screen or use the phone icon at the bottom of the app to connect with an available stylist right now.',
          },
          {
            question: 'How do in-person sessions work?',
            answer:
              'Sessions take place at a location you provide. Stylists review requests based on their availability and distance, and may accept or decline at their discretion. EMME reserves the right to cancel or intervene in any session if safety or community concerns arise.',
          },
          {
            question: 'Can I message my stylist?',
            answer:
              'Yes, before and after your session. You can also purchase Backend Hours for additional styling work outside of your session.',
          },
          {
            question: 'What are Backend Hours?',
            answer:
              'Paid time for your stylist to create Lookbooks, source pieces, or plan your wardrobe. Deliverables are sent directly through chat.',
          },
        ],
      },
      {
        label: 'PRICING & CANCELLATIONS',
        items: [
          {
            question: 'How much does a session cost?',
            answer:
              "Session pricing varies by stylist. You'll see rates clearly listed on each stylist's profile before booking.",
          },
          {
            question: 'What is the cancelation policy?',
            answer:
              'Sessions can be canceled up to 24 hours in advance. Same-day bookings and late cancellations are non-refundable.',
          },
        ],
      },
      {
        label: 'PRIVACY & SAFETY',
        items: [
          {
            question: 'Who can see my location?',
            answer:
              'Your full address is only shared with the stylist confirmed for your in-person session. It is never public, searchable, or indexed.',
          },
        ],
      },
      {
        label: 'SUPPORT',
        items: [
          {
            question: 'How do I get help?',
            answer: 'Email support@emmestyling.com.',
          },
          {
            question: 'How do I delete my account?',
            answer:
              'Under settings in the app you can select "Delete Account." Your account and all associated data will be permanently deleted within 7 days.',
          },
        ],
      },
    ],
  },
  {
    title: 'STYLISTS:',
    categories: [
      {
        label: 'GETTING STARTED',
        items: [
          {
            question: 'Where is EMME available for stylists?',
            answer: 'Stylists can access EMME on both mobile and web.',
          },
          {
            question: 'How do I become a stylist on EMME?',
            answerSegments: [
              { kind: 'text', text: 'You can ' },
              { kind: 'applyLink' },
              {
                kind: 'text',
                text: " Accepted applicants will receive an invitation email with onboarding instructions. If you don't receive it, check your spam folder or reach out to support@emmestyling.com.",
              },
            ],
          },
        ],
      },
      {
        label: 'AVAILABILITY & BOOKING',
        items: [
          {
            question: 'How do I manage my availability?',
            answer:
              'Set and sync your availability under the Set Availability tab. For virtual sessions, the earliest bookable slot must be at least one hour out. In-person session requests must be approved in advance.',
          },
          {
            question: 'How do in-person sessions work?',
            answer:
              "You'll receive requests based on your availability and may accept or decline at your discretion. EMME reserves the right to cancel or intervene in any session if safety or community concerns arise.",
          },
        ],
      },
      {
        label: 'DURING A SESSION',
        items: [
          {
            question: 'How do I join a session?',
            answer:
              'Go to the Upcoming Sessions tab on desktop or mobile and select Join Call. All video sessions run directly within EMME. If a client disconnects, wait a few minutes–they can rejoin up until five minutes before the scheduled end time. A prompt will appear two minutes before the session ends with the option to extend, with additional time billed per minute for up to 30 minutes.',
          },
          {
            question: 'What is Instant Styling?',
            answer:
              "Toggle Instant Styling on from your mobile app or web dashboard to receive real-time client requests. If a request isn't accepted within 60 seconds, it routes to the next available stylist.",
          },
        ],
      },
      {
        label: 'MESSAGING & BACKEND HOURS',
        items: [
          {
            question: 'How does messaging work?',
            answer:
              'Message clients before or after sessions to share notes, follow-ups, or offer Backend Hours.',
          },
          {
            question: 'What are Backend Hours?',
            answer:
              'Paid post-session time for additional services — Lookbooks, sourcing, wardrobe planning, and more. Send a request by entering a custom amount under the Backend Hours tab in your client chat.',
          },
        ],
      },
      {
        label: 'PAYMENTS',
        items: [
          {
            question: 'How do I get paid?',
            answer:
              'Payouts are processed via Stripe Connect within 72 hours of a completed session.',
          },
          {
            question: 'How do I update my rate?',
            answer:
              'Hourly rates are managed by the EMME team. To request a review, contact support@emmestyling.com.',
          },
        ],
      },
      {
        label: 'CANCELLATIONS',
        items: [
          {
            question: 'What is the cancellation policy?',
            answer:
              'Repeated last-minute cancellations or no-shows will negatively impact your account standing.',
          },
        ],
      },
    ],
  },
];
